import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Pagination } from 'app/_models/pagination.model';
import { NotificationService } from 'app/_services/notification.service';
import { ScheduleValidationService } from 'app/_services/schedule-validation.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import { SiteFormsService } from 'app/_services/site-forms.service';
import { SiteService } from 'app/_services/site.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { PlausibleAnalyticsService } from 'app/_services/plausible-analytics.service';

@Component({
  selector: 'work-order-form',
  templateUrl: './work-order-form.component.html',
})
export class WorkOrderFormComponent implements OnInit {
  public selectedSiteId: any;
  public siteOwnerId: any;
  public allowCompulsoryCheckOnSiteForm: boolean;
  public allowCompulsoryCheckOffSiteForm: boolean;
  public allowWorkOrderScheduling: boolean;
  public allowAssetScheduling: boolean;

  public pageContext = 'work-order-form';
  public saveInProgress = false;

  public selectedSite: any;

  public form: FormGroup;
  public validationErrors: any[] = [];
  public minDate = new Date();

  public assets: any[] = [];
  public selectedDocuments: any[] = [];
  public siteFormsOptions: any[] = [];
  public assetsLoading = true;
  public siteFormsOptionsLoading = true;
  public siteConfigLoading = true;

  public exemptFromAssetScheduling: boolean;

  constructor(
    private notificationService: NotificationService,
    private workOrderService: SiteWorkOrdersService,
    private assetsService: SiteAssetsService,
    private siteFormsService: SiteFormsService,
    private scheduleValidationService: ScheduleValidationService,
    private siteService: SiteService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private plausibleService: PlausibleAnalyticsService,
  ) { }

  ngOnInit(): void {
    this.minDate.setUTCHours(0, 0, 0, 0);

    this.form = this.fb.group({
      asset: new FormControl('', [Validators.required]),
      description: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      minPartySize: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(30)]),
      scheduledStartTime: new FormControl(),
      scheduledEndTime: new FormControl(),
      exemptFromAssetScheduling: new FormControl(),
      checkOnSiteForm: new FormControl(),
      checkOffSiteForm: new FormControl(),
    });

    this.selectedSiteId = this.getSiteIdFromRoute();
    if (!this.selectedSiteId) {
      this.notificationService.notify('danger', 'Missing site id for work order creation.');
      this.siteConfigLoading = false;
      return;
    }
    this.setUpSiteSpecificData();
  }

  public get pageLoading(): boolean {
    return this.siteConfigLoading || this.assetsLoading || this.siteFormsOptionsLoading;
  }

  private getSiteIdFromRoute(): any {
    const directId = this.activatedRoute.snapshot.params.id;
    if (directId) {
      return directId;
    }

    if (this.activatedRoute.parent && this.activatedRoute.parent.snapshot) {
      const parentId = this.activatedRoute.parent.snapshot.params.id;
      if (parentId) {
        return parentId;
      }
    }

    if (this.activatedRoute.parent && this.activatedRoute.parent.parent && this.activatedRoute.parent.parent.snapshot) {
      return this.activatedRoute.parent.parent.snapshot.params.id;
    }

    return null;
  }

  private setUpSiteSpecificData() {
    this.siteConfigLoading = true;
    this.siteService.getSiteById(this.selectedSiteId).subscribe(
      (data: any) => {
        this.selectedSite = data;
        this.siteOwnerId = data.siteOwner.id;
        this.allowCompulsoryCheckOnSiteForm = data.siteOwner.siteOwnerUser.allowCompulsoryCheckOnSiteForm;
        this.allowCompulsoryCheckOffSiteForm = data.siteOwner.siteOwnerUser.allowCompulsoryCheckOffSiteForm;
        this.allowWorkOrderScheduling = data.siteOwner.siteOwnerUser.allowWorkOrderScheduling;
        this.allowAssetScheduling = data.siteOwner.siteOwnerUser.allowAssetScheduling;

        this.getAllAssets();
        this.getAllSiteForms();
      },
      (error) => {
        this.siteConfigLoading = false;
        throw error;
      },
      () => {
        this.siteConfigLoading = false;
      }
    );
  }

  private getAllAssets() {
    this.assetsLoading = true;
    this.assets = [];
    this.assetsService.getAllSiteAssetsNoPagination(this.selectedSiteId).subscribe(
      (data: any) => {
        data.forEach((element) => {
          this.assets.push({
            label: element.name,
            value: element,
          });
        });
      },
      (error) => {
        this.assetsLoading = false;
        throw error;
      },
      () => {
        this.assetsLoading = false;
      }
    );
  }

  private getAllSiteForms() {
    this.siteFormsOptionsLoading = true;
    this.siteFormsOptions = [];
    const pagination: Pagination = {
      size: 10000,
      page: 0,
      totalRecords: 0,
    };

    this.siteFormsService.getAllBySite(this.selectedSiteId, pagination, ['siteForm.name']).subscribe(
      (siteForms: any) => {
        this.siteFormsOptions.push({
          label: 'Select Site Form...',
          value: null,
        });
        siteForms.body.forEach((element) => {
          const item = {
            label: element.name,
            value: element.id,
          };
          this.siteFormsOptions.push(item);
        });
      },
      (error) => {
        this.siteFormsOptionsLoading = false;
        throw error;
      },
      () => {
        this.siteFormsOptionsLoading = false;
      }
    );
  }

  public submit() {
    this.trackButtonClick('btn-submit-create')
    this.saveInProgress = true;
    const values = this.form.value;

    this.validationErrors = [];
    const validation = this.scheduleValidationService.validateForm(values);

    if (!validation.isValid) {
      this.validationErrors = validation.validationErrors;
      this.saveInProgress = false;
      return;
    }

    // if the asset is exempt, this work order exempt tickbox is hidden. send false for consistency
    const workOrderExempt = this.exemptFromAssetScheduling ? false : values.exemptFromAssetScheduling;

    const body = {
      assetId: values.asset.value.id,
      description: values.description,
      minimumPartySize: values.minPartySize,
      checkOnSiteFormId: values.checkOnSiteForm,
      checkOffSiteFormId: values.checkOffSiteForm,
      scheduledStartTime: values.scheduledStartTime,
      scheduledEndTime: values.scheduledEndTime,
      exemptFromAssetScheduling: workOrderExempt,
      status: 'AVAILABLE',
      ramsArray: this.selectedDocuments,
    };

    this.workOrderService.createWorkOrder(body).subscribe(
      (createdWorkOrder: any) => {
        this.saveInProgress = false;
        const createdWorkOrderId = createdWorkOrder ? createdWorkOrder.id : null;
        if (createdWorkOrderId) {
          this.router.navigate(['../', createdWorkOrderId], {
            relativeTo: this.activatedRoute,
            queryParams: { created: '1' },
          });
          return;
        }

        this.navigateBackToList();
      },
      (error) => {
        this.saveInProgress = false;
        throw error;
      }
    );
  }

  public onFileChange(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      this.selectedDocuments.push(file);
    }

    event.target.value = '';
  }

  public viewSelectedDocument(file) {
    const fileUrl = URL.createObjectURL(file);
    window.open(fileUrl, '_blank');
    window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
  }

  public removeSelectedDocument(index: number) {
    this.selectedDocuments.splice(index, 1);
  }

  public navigateBackToList() {
    this.router.navigate(['../'], { relativeTo: this.activatedRoute });
  }

  public selectedAssetChange(asset) {
    this.exemptFromAssetScheduling = asset.value.value.exemptFromAssetScheduling;
  }

  public trackButtonClick(buttonName: string) {
    const eventName = `${buttonName}_${this.pageContext}`;
    this.plausibleService.trackButtonClick(eventName);
  }
}
